import { OrderTypes, TakeAwayProps, TypeOfPayment } from '@/interfaces/models/order';
import Client from '@/models/client';
import OrderProduct from '@/models/orderProduct';
import OrderProductIngredient from '@/models/orderProductIngredient';
import OrderStaff from '@/models/orderStaff';
import i18n from '@/translations/i18n';
import stringFormat from '@/utils/format/string';
import numberUtils from '@/utils/number.utils';
import PlaceDate from '@/utils/placeDate';
import SystemDate from '@/utils/systemDate';
import VueI18n from 'vue-i18n';
import Review from './review';

export default class TakeAway {
  client: null | Client = null;
  comment: null | string = null;
  createdAt: number = 0;
  cookingAt: number | null = null;
  datetime: number = 0;
  discount: number | null = null;
  discountAmount: number = 0;
  doneAt: number | null = null;
  id: string = '';
  isCancel: boolean = false;
  isCooking: boolean = false;
  isDone: boolean = false;
  isPrinted: boolean = false;
  paidOnline: boolean = false;
  isRefund: boolean = false;
  isSave: boolean = false;
  isWidget: boolean = false;
  products: OrderProduct[] = [];
  orderNumber: string = '0';
  orderNumberInQueue: string = '000';
  orderType: number = OrderTypes.TakeAway;
  printError: string | null = null;
  placeId: string | undefined = undefined;
  review: null | Review = null;
  staff: null | OrderStaff = null;
  systemOrderNumber: number = 0;
  totalSpent: number = 0;
  typeOfPayment: null | TypeOfPayment = null;
  placeName: string | undefined = undefined;

  public static createFromRequest(takeAwayProps: TakeAwayProps): TakeAway {
    const takeAway = new TakeAway();

    if (takeAwayProps.client) {
      takeAway.client = Client.createFromRequest(takeAwayProps.client);
    }

    takeAway.comment = takeAwayProps.comment;
    takeAway.createdAt = takeAwayProps.createdAt;
    takeAway.cookingAt = takeAwayProps.cookingAt;
    takeAway.datetime = takeAwayProps.datetime;
    takeAway.discount = takeAwayProps.discount;
    takeAway.discountAmount = takeAwayProps.discountAmount;
    takeAway.doneAt = takeAwayProps.doneAt;
    takeAway.id = takeAwayProps.id;
    takeAway.isCancel = takeAwayProps.isCancel;
    takeAway.isCooking = takeAwayProps.isCooking;
    takeAway.isDone = takeAwayProps.isDone;
    takeAway.isPrinted = takeAwayProps.isPrinted;
    takeAway.paidOnline = takeAwayProps.paidOnline;
    takeAway.isRefund = takeAwayProps.isRefund;
    takeAway.isSave = takeAwayProps.isSave;
    takeAway.isWidget = takeAwayProps.isWidget;
    takeAway.products = takeAwayProps.products.map(OrderProduct.createFromRequest);
    takeAway.orderNumber = takeAwayProps.orderNumber;
    takeAway.orderNumberInQueue = takeAwayProps.orderNumberInQueue;
    takeAway.orderType = takeAwayProps.orderType;
    takeAway.placeId = takeAwayProps.placeId;
    takeAway.placeName = takeAwayProps.placeName;
    takeAway.printError = takeAwayProps.printError;

    if (takeAwayProps.review) {
      takeAway.review = new Review(takeAwayProps.review);
    }

    takeAway.systemOrderNumber = takeAwayProps.systemOrderNumber;
    takeAway.totalSpent = takeAwayProps.totalSpent;
    takeAway.typeOfPayment = takeAwayProps.typeOfPayment;

    if (takeAwayProps.staff) {
      takeAway.staff = new OrderStaff(takeAwayProps.staff);
    }

    return takeAway;
  }

  get humanOrderType(): VueI18n.TranslateResult {
    if (this.orderType === OrderTypes.Terminal) {
      return 'Терминал';
    }

    return i18n.t('dialog.take_away.view.title');
  }

  get date(): PlaceDate {
    return new PlaceDate(this.datetime);
  }

  get createdAtDate(): PlaceDate {
    return new PlaceDate(this.createdAt);
  }

  get timeOfCompletion(): string | null {
    if (null !== this.cookingAt && null !== this.doneAt) {
      const timeInSeconds = this.doneAt - this.cookingAt;
      const countOfMinutes = Math.floor(timeInSeconds / 60);
      const countOfSeconds = stringFormat.t2(timeInSeconds % 60);

      if (countOfMinutes > 0) {
        return `${countOfMinutes}м ${countOfSeconds}с`;
      }

      return `${countOfSeconds}с`;
    }

    if (null !== this.cookingAt && null === this.doneAt) {
      return 'Выполняется';
    }

    return null;
  }

  get isAllProductsChecked(): boolean {
    return this.products.every(({isDone}: OrderProduct) => isDone);
  }

  get humanOrderNumber() {
    return this.isSave ? '\u2013' : `${this.orderNumber}`;
  }

  get shortHumanOrderNumber() {
    return this.isSave ? '\u2013' : `#${this.orderNumberInQueue}`;
  }

  get cost() {
    return this.products.reduce<number>((
      totalCost: number,
      {cost, count}: OrderProduct) => {
        return totalCost + cost * count;
      },
      0,
    );
  }

  get isReservation() {
    return this.orderType === 0;
  }

  get isTakeAway() {
    return this.orderType === 1;
  }

  get isTerminal() {
    return this.orderType === 2;
  }

  get isAssigned() {
    return this.staff !== null;
  }

  public calcDiscount(selectedDiscount: number) {
    const discount = this.products.reduce(
      function (discount: number, product: OrderProduct): number {
        if (!product.product) {
          return discount;
        }

        const ingredientDiscount = product.ingredients.reduce(
          (discount: number, ingredient: OrderProductIngredient): number => {
            return discount + numberUtils.correctFloatNumber(numberUtils.calculateDiscount(ingredient.cost + ingredient.discountAmount, selectedDiscount) * ingredient.count);
          },
          .0
        );

        const productDiscount = numberUtils.correctFloatNumber(numberUtils.calculateDiscount(product.productCost, selectedDiscount) + ingredientDiscount);

        return discount + numberUtils.correctFloatNumber(product.count * productDiscount);
      },
      .0,
    );

    return numberUtils.correctFloatNumber(discount);
  }

  public take(staff: OrderStaff) {
    this.isCooking = true;
    this.staff = staff;
  }

  public refuse() {
    this.isCooking = false;
    this.staff = null;
  }

  public done() {
    this.isDone = true;
    this.isCooking = false;
  }

  public undone() {
    this.isDone = false;
    this.isCooking = true;
  }

  public doneProduct(productId: string) {
    const product = this.products.find(({id}: OrderProduct) => id === productId);

    if (product !== undefined) {
      product.done();
    }
  }

  public undoneProduct(productId: string) {
    const product = this.products.find(({id}: OrderProduct) => id === productId);

    if (product !== undefined) {
      product.undone();
    }
  }

  public isActualOrderByToday(date: SystemDate): boolean {
    return !this.isDone || this.date.getLocalTime() >= date.getLocalTime();
  }

  public removeClient() {
    this.client = null;
  }

  public getWriteOffPayload() {
    return this.products.reduce<any>(
      (products: any, product: OrderProduct) => {
        if (product.product && product.product.projectProduct) {
          if (products.product.hasOwnProperty(product.product.projectProduct.id)) {
            products.product[product.product.projectProduct.id] += product.count;
          } else {
            products.product[product.product.projectProduct.id] = product.count;
          }

          for (const orderProductIngredient of product.ingredients) {
            if (orderProductIngredient.ingredient && orderProductIngredient.ingredient.projectIngredient) {
              if (products.ingredient.hasOwnProperty(orderProductIngredient.ingredient.projectIngredient.id)) {
                products.ingredient[orderProductIngredient.ingredient.projectIngredient.id] += orderProductIngredient.count * product.count;
              } else {
                products.ingredient[orderProductIngredient.ingredient.projectIngredient.id] = orderProductIngredient.count * product.count;
              }
            }
          }
        }

        return products;
      },
      {
        product: {},
        ingredient: {},
      }
    );
  }

  public getNegativeWriteOffPayload() {
    return this.products.reduce<any>(
      (products: any, product: OrderProduct) => {
        if (product.product && product.product.projectProduct) {
          if (products.product.hasOwnProperty(product.product.projectProduct.id)) {
            products.product[product.product.projectProduct.id] -= product.count;
          } else {
            products.product[product.product.projectProduct.id] = -product.count;
          }

          for (const orderProductIngredient of product.ingredients) {
            if (orderProductIngredient.ingredient && orderProductIngredient.ingredient.projectIngredient) {
              if (products.ingredient.hasOwnProperty(product.product.projectProduct.id)) {
                products.ingredient[orderProductIngredient.ingredient.projectProduct.id] -= orderProductIngredient.count;
              } else {
                products.ingredient[orderProductIngredient.ingredient.projectProduct.id] = -orderProductIngredient.count;
              }
            }
          }
        }

        return products;
      },
      {
        product: {},
        ingredient: {},
      }
    );
  }
}
